<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  LayoutDashboard, ShoppingBag, Package, User, Menu, X, Home,
  ChefHat, Truck, Calendar, CalendarDays, Tag, Mail, PieChart, LogOut, GraduationCap, Layers, BookOpen, Star, MessageSquare, PartyPopper
} from 'lucide-vue-next';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { isNative } from '@/utils/platform';
import { useAuthStore } from '@/stores/auth';

const authStore = useAuthStore();
const router = useRouter();
const handleLogout = async () => {
  showDrawer.value = false;
  await authStore.logout();
  router.replace('/auth/connexion');
};

const route = useRoute();
const navRef = ref(null);
const pillStyle = ref({ opacity: 0 });
const showDrawer = ref(false);
const isDriverMode = computed(() => route.query.mode === 'driver');

const allAdminLinks = [
  { label: 'Dash', path: '/admin', icon: LayoutDashboard },
  { label: 'Commandes', path: '/admin/commandes', icon: ShoppingBag },
  { label: 'Planning', path: '/admin/planning', icon: CalendarDays },
  { label: 'Catalogue', path: '/admin/catalogue', icon: BookOpen },
  { label: 'Production', path: '/admin/productions-v2', icon: ChefHat },
  { label: 'Stock', path: '/admin/stock', icon: Package },
  { label: 'Livraison', path: '/admin/livraison', icon: Truck },
  { label: 'Clients', path: '/admin/clients', icon: User },
  { label: 'Promos', path: '/admin/promos', icon: Tag },
  { label: 'Ateliers', path: '/admin/ateliers', icon: GraduationCap },
  { label: 'Créneaux', path: '/admin/creneaux', icon: Calendar },
  { label: 'Comms', path: '/admin/communication', icon: Mail },
  { label: 'Stats', path: '/admin/statistiques', icon: PieChart },
  { label: 'Avis', path: '/admin/avis', icon: Star },
  { label: 'Contacts', path: '/admin/contacts', icon: MessageSquare },
  { label: 'Inauguration', path: '/admin/inauguration', icon: PartyPopper },
];

const deliveryCount = ref(0);
let unsubDelivery = null;
const contactCount = ref(0);
let unsubContacts = null;

const finalAdminLinks = computed(() => {
  return allAdminLinks.map(link => {
    if (link.path === '/admin/livraison') {
      return { ...link, badge: deliveryCount.value > 0 ? deliveryCount.value : null };
    }
    if (link.path === '/admin/contacts') {
      return { ...link, badge: contactCount.value > 0 ? contactCount.value : null };
    }
    return link;
  });
});

// Pill measurement
const measureAndApply = () => {
  if (!navRef.value) return;
  const activeEl = navRef.value.querySelector('.nav-active');
  if (activeEl) {
    const { offsetLeft, offsetWidth, offsetTop, offsetHeight } = activeEl;
    const px = 4;
    pillStyle.value = {
      left: `${offsetLeft + px / 2}px`,
      width: `${offsetWidth - px}px`,
      height: `${offsetHeight}px`,
      top: `${offsetTop}px`,
      opacity: 1
    };
  } else {
    pillStyle.value = { opacity: 0 };
  }
};

const updateIndicators = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(measureAndApply);
    });
  });
};

let resizeTimer;
const debouncedUpdate = () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(updateIndicators, 120);
};

watch(() => route.path, updateIndicators);

onMounted(() => {
  window.addEventListener('resize', debouncedUpdate);
  window.addEventListener('orientationchange', () => {
    setTimeout(updateIndicators, 200);
    setTimeout(updateIndicators, 500);
  });
  setTimeout(updateIndicators, 100);

  // Delivery badge listener
  try {
    const q = query(
      collection(db, 'orders'),
      where('status', 'in', ['shipping', 'delivering', 'en livraison', 'shipped'])
    );
    unsubDelivery = onSnapshot(q, (snap) => {
      deliveryCount.value = snap.size;
    }, (error) => {
      console.warn("Delivery Badge Listener paused:", error.message);
    });
  } catch (e) {
    console.error("Error setting up delivery badge listener", e);
  }

  // Contact badge listener
  try {
    const qc = query(
      collection(db, 'contacts'),
      where('status', '==', 'new')
    );
    unsubContacts = onSnapshot(qc, (snap) => {
      contactCount.value = snap.size;
    }, (error) => {
      console.warn("Contact Badge Listener paused:", error.message);
    });
  } catch (e) {
    console.error("Error setting up contact badge listener", e);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', debouncedUpdate);
  if (unsubDelivery) unsubDelivery();
  if (unsubContacts) unsubContacts();
  clearTimeout(resizeTimer);
});
</script>

<template>
  <!-- Mobile Dock (Admin) -->
  <div class="fixed bottom-0 left-0 right-0 z-50 md:hidden">
    <div class="liquid-glass-dock pb-[calc(env(safe-area-inset-bottom)+5px)] pt-2 px-4 relative">
      <div class="flex items-end justify-between relative z-10 h-14" ref="navRef">
        <!-- Sliding Pill -->
        <div
          class="absolute bg-[#0077B6] rounded-2xl transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] -z-10"
          :style="pillStyle"
        ></div>

        <template v-if="!isDriverMode">
          <!-- Dashboard -->
          <RouterLink
            to="/admin"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
            :class="{ 'nav-active text-white': route.path === '/admin' }"
          >
            <LayoutDashboard class="h-6 w-6" />
            <span class="text-[10px] font-bold">Dash</span>
          </RouterLink>

          <!-- Commandes -->
          <RouterLink
            to="/admin/commandes"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
            :class="{ 'nav-active text-white': route.path.startsWith('/admin/commandes') }"
          >
            <ShoppingBag class="h-6 w-6" />
            <span class="text-[10px] font-bold">Cmmdes</span>
          </RouterLink>

          <!-- Center Button (Menu) -->
          <div class="relative -top-6 z-30">
            <button
              @click="showDrawer = true"
              class="w-14 h-14 rounded-full bg-[#0077B6] text-white shadow-lg shadow-[#0077B6]/40 flex items-center justify-center transform transition-transform active:scale-95 border-4 border-white"
            >
              <Menu class="h-6 w-6" />
            </button>
            <span class="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[10px] font-bold text-gray-900 whitespace-nowrap">Menu</span>
          </div>

          <!-- Clients -->
          <RouterLink
            to="/admin/clients"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
            :class="{ 'nav-active text-white': route.path.startsWith('/admin/clients') }"
          >
            <User class="h-6 w-6" />
            <span class="text-[10px] font-bold">Clients</span>
          </RouterLink>

          <!-- Planning -->
          <RouterLink
            to="/admin/planning"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
            :class="{ 'nav-active text-white': route.path.startsWith('/admin/planning') }"
          >
            <CalendarDays class="h-6 w-6" />
            <span class="text-[10px] font-bold">Planning</span>
          </RouterLink>
        </template>

        <!-- DRIVER MODE -->
        <template v-else>
          <RouterLink
            to="/admin"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
            :class="{ 'nav-active text-white': route.path === '/admin' }"
          >
            <LayoutDashboard class="h-6 w-6" />
            <span class="text-[10px] font-bold">Dash</span>
          </RouterLink>

          <RouterLink
            to="/admin/commandes"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
            :class="{ 'nav-active text-white': route.path.startsWith('/admin/commandes') }"
          >
            <ShoppingBag class="h-6 w-6" />
            <span class="text-[10px] font-bold">Cmmdes</span>
          </RouterLink>

          <!-- Center Button (RETURN TO DELIVERY) -->
          <div class="relative -top-6 z-30">
            <RouterLink
              :to="{ path: '/admin/livraison', query: { mode: 'driver' }}"
              class="w-14 h-14 rounded-full bg-[#e94591] text-white shadow-lg shadow-[#e94591]/40 flex items-center justify-center transform transition-transform active:scale-95 border-4 border-white animate-pulse"
            >
              <Truck class="h-6 w-6" />
            </RouterLink>
            <span class="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[10px] font-bold text-gray-900 whitespace-nowrap">Tournée</span>
          </div>

          <RouterLink
            to="/admin/clients"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
            :class="{ 'nav-active text-white': route.path.startsWith('/admin/clients') }"
          >
            <User class="h-6 w-6" />
            <span class="text-[10px] font-bold">Clients</span>
          </RouterLink>

          <button
            @click="showDrawer = true"
            class="flex-1 flex flex-col items-center justify-center gap-1 h-full text-gray-400 hover:text-[#0077B6] transition-colors relative z-20"
          >
            <Menu class="h-6 w-6" />
            <span class="text-[10px] font-bold">Menu</span>
          </button>
        </template>
      </div>
    </div>
  </div>

  <!-- Admin Mobile Drawer -->
  <div v-if="showDrawer" class="fixed inset-0 z-[60] md:hidden">
    <div class="absolute inset-0 bg-black/20" @click="showDrawer = false"></div>
    <div class="absolute bottom-0 left-0 right-0 bg-white rounded-t-[32px] p-6 animate-slide-up">
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-xl font-bold text-gray-900 font-baloo">Menu Admin</h3>
        <button @click="showDrawer = false" class="p-2 bg-gray-100 rounded-full">
          <X class="w-5 h-5 text-gray-500" />
        </button>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <RouterLink
          v-for="link in finalAdminLinks"
          :key="link.path"
          :to="link.path"
          @click="showDrawer = false"
          class="flex flex-col items-center gap-2 p-2 rounded-xl active:bg-gray-50 relative"
        >
          <div class="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-500 relative">
            <component :is="link.icon" class="w-6 h-6" />
            <div
              v-if="link.badge"
              class="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white ring-2 ring-white animate-pulse"
            >
              {{ link.badge }}
            </div>
          </div>
          <span class="text-xs font-bold text-gray-600 text-center">{{ link.label }}</span>
        </RouterLink>

        <div class="col-span-4 h-px bg-gray-100 my-2"></div>

        <RouterLink v-if="!isNative" to="/" class="col-span-4 flex items-center gap-3 p-4 bg-gray-50 text-gray-600 rounded-2xl font-bold">
          <Home class="w-5 h-5" /> Retour au site client
        </RouterLink>

        <button @click="handleLogout" class="col-span-4 flex items-center gap-3 p-4 bg-red-50 text-red-600 rounded-2xl font-bold active:bg-red-100 transition-colors">
          <LogOut class="w-5 h-5" /> Déconnexion
        </button>
      </div>
    </div>
  </div>
</template>
